import { LoadingButton } from "@mui/lab";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  List,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import ReactCodeInput from "react-code-input";
import { toast } from "react-toastify";
import Iconify from "src/components/Iconify";
import axiosInstance from "src/utils/axios";

interface ValidateCodeFormProps {
  open: boolean;
  onClose: () => void;
  onResendCode: () => void;
  onSubmit: (code: string) => Promise<void>;
}

const ValidateCodeForm: React.FC<ValidateCodeFormProps> = ({
  open,
  onClose,
  onResendCode,
  onSubmit,
}) => {
  const theme = useTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(60);
  const [code, setCode] = useState("");

  const dialogStyle = { "& .MuiDialog-paper": { backgroundColor: theme.palette.grey[50] } };

  const handleClose = (event: {}, reason: "backdropClick" | "escapeKeyDown") => {
    if (reason !== "backdropClick") {
      onClose();
    }
  };

  async function handleSubmit(code: string) {
    try {
      setIsSubmitting(true);
      await onSubmit(code);
    } catch (err) {
      toast.error(
        err.message || "Não foi possível enviar o código. Por favor, tente novamente mais tarde.",
        {
          position: toast.POSITION.TOP_RIGHT,
          theme: "colored",
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  }

  useEffect(() => {
    if (!isCodeSent) {
      setResendCountdown(60);
      return;
    }

    setResendCountdown(60);

    const intervalId = window.setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          window.clearInterval(intervalId);
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [isCodeSent]);

  const BackButton = () => {
    const buttonIcon = isCodeSent ? "mdi:arrow-left" : "mdi:close";
    const handleButtonClick = isCodeSent ? () => setIsCodeSent(false) : onClose;

    return (
      <Button
        variant="text"
        onClick={handleButtonClick}
        sx={{
          position: "absolute",
          left: 12,
          top: 12,
          minWidth: 0,
          color: theme.palette.primary.main,
          fontWeight: "bold",
          textTransform: "none",
          fontSize: "15px",
          borderRadius: "50%",
          width: "36px",
          height: "36px",
          padding: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          "&:hover": {
            backgroundColor: "#e0e0e0",
          },
        }}
      >
        <Iconify icon={buttonIcon} width={18} height={18} />
      </Button>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth sx={dialogStyle}>
      <DialogTitle align="center">
        <Box display="flex" flexDirection="column" alignItems="center">
          <BackButton />
          <Iconify
            icon="mdi:shield-user-outline"
            width={64}
            height={64}
            mb={1}
            color={theme.palette.secondary.main}
          />
          <Typography
            color={theme.palette.primary.main}
            fontFamily="Open Sans"
            fontSize="24px"
            marginRight="8px"
          >
            Verificação
          </Typography>
          <Typography variant="subtitle2" color={theme.palette.grey[500]} marginTop="10px">
            Para continuar com o cadastro, confirme seu código de verificação que foi enviado para
            seu celular.
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent style={{ marginTop: "15px" }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <ReactCodeInput
            type="number"
            fields={6}
            name={"code"}
            inputMode={"numeric"}
            onChange={(value: string) => setCode(value)}
          />
          <LoadingButton
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
            onClick={() => handleSubmit(code)}
          >
            Confirmar código
          </LoadingButton>
          {resendCountdown > 0 ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              marginTop={4}
              color={theme.palette.grey[500]}
            >
              <Iconify icon="mdi:clock-outline" width={20} height={20} mr={1} />
              <Typography variant="body2" textAlign="center" fontWeight="bold">
                Reenviar disponível em {Math.floor(resendCountdown / 60)}:
                {String(resendCountdown % 60).padStart(2, "0")}
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography
                variant="body2"
                color={theme.palette.grey[500]}
                textAlign="center"
                marginTop={4}
                fontWeight="bold"
              >
                Não recebeu o código?
              </Typography>
              <Button variant="text" onClick={onResendCode}>
                <Iconify icon="mdi:refresh" width={24} height={24} mr={1} />
                Clique aqui para reenviar.
              </Button>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ValidateCodeForm;

